/**
 * Client-readable mirror of the viewer's device time zone, written browser-side
 * by `TimeZoneProvider` once resolved and read server-side by DALs that need to
 * convert a *picked calendar day* (not an instant) into an absolute time range —
 * e.g. the recordings date filter, where "today" must mean the viewer's local
 * today, not the UTC day.
 *
 * Deliberately not `httpOnly`: the browser writes it, which is what keeps the
 * device zone off the request critical path (see `TimeZoneProvider`'s doc
 * comment on why it isn't resolved in `getRequestConfig`). Tampering only skews
 * the tamperer's own date-range queries, and every read validates against
 * `Intl.DateTimeFormat`, so the value is never trusted beyond "which zone to
 * compute a day boundary in".
 */
export const TIME_ZONE_COOKIE = "siro-timezone"

/**
 * Vercel's edge-injected IP-geolocation time zone. Only ever a fallback for the
 * cookie — see `getServerTimeZone` for why it is consulted at all.
 */
export const VERCEL_TIME_ZONE_HEADER = "x-vercel-ip-timezone"

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

const isValidTimeZone = (value: string): boolean => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value })
    return true
  } catch {
    return false
  }
}

/** Validates an IANA zone name from any untrusted source (cookie or header). */
export const parseTimeZone = (
  value: string | undefined | null,
): string | null => (value && isValidTimeZone(value) ? value : null)

/** Browser-only. */
export const writeTimeZoneCookie = (timeZone: string): void => {
  // biome-ignore lint/suspicious/noDocumentCookie: the suggested Cookie Store API is unsupported in Safari and Firefox; this cookie must be set on every browser.
  document.cookie = `${TIME_ZONE_COOKIE}=${timeZone}; path=/; max-age=${ONE_YEAR_SECONDS}; samesite=lax`
}
